'use client';

import CTAButton from '@/components/CTAButton';
import HeroSection from '@/components/HeroSection';
import Footer from '@/components/Footer';

export default function CookiesPage() {
  return (
    <main className="portfolio-page legal-page">
      <HeroSection
        title="Cookies and privacy."
        subtitle="How this site uses cookies, processes personal data, and protects your choices."
        className="inner-hero"
      />

      {/* Content Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          {/* Intro */}
          <div className="mb-20 max-w-3xl">
            <p className="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed">
              At <span className="font-semibold text-gray-900">BMateas.com</span>, we respect your privacy and are committed to being transparent about how we use cookies and personal data. This policy explains what cookies are, which ones we use, your rights, and how to manage your preferences.
            </p>
          </div>

          {/* Services Section */}
          <div className="mb-20">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-12">Services & Data Processing</h2>

            {/* Google Analytics */}
            <div className="mb-16 pb-16 border-b border-gray-200">
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-6">Google Analytics (GA4)</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    Purpose: Improving the website and marketing campaigns. GA4 records key events (page views, add-to-cart, etc.) and integrates with Google Ads to build remarketing audiences and measure conversions.
                  </p>
                  <div className="space-y-4">
                    <div>
                      <p className="font-semibold text-gray-900 mb-2">Legal basis:</p>
                      <p className="text-gray-700">Your consent to analytics or marketing processing.</p>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-2">Retention period:</p>
                      <p className="text-gray-700">14 months</p>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="space-y-4">
                    <div>
                      <p className="font-semibold text-gray-900 mb-2">Data processed:</p>
                      <ul className="text-gray-700 space-y-2 ml-4">
                        <li>• Traffic source (referrer, URL)</li>
                        <li>• Pages viewed & events</li>
                        <li>• Product and order information</li>
                        <li>• Internal user ID</li>
                        <li>• HTTP identifiers (anonymised IP, cookies, screen resolution, device, browser, country, language)</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-2">Processor:</p>
                      <p className="text-gray-700">Google Ireland Limited (Reg. No. 368047)</p>
                    </div>
                    <p className="text-sm text-blue-600">
                      <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer" className="hover:underline">
                        View Terms →
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Microsoft Clarity */}
            <div className="mb-16 pb-16 border-b border-gray-200">
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-6">Microsoft Clarity</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    The Microsoft Clarity service helps us understand user behavior (clicking, scrolling, form usage) and adapt the website to your needs. It is activated only after you have consented to analytics cookies.
                  </p>
                  <div className="space-y-4">
                    <div>
                      <p className="font-semibold text-gray-900 mb-2">Data protection:</p>
                      <p className="text-gray-700">Data are stored in a pseudonymised profile and cannot be used to identify individual persons.</p>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="space-y-4">
                    <div>
                      <p className="font-semibold text-gray-900 mb-2">Data recorded:</p>
                      <ul className="text-gray-700 space-y-2 ml-4">
                        <li>• Clicks and movement tracking</li>
                        <li>• Anonymised IP address</li>
                        <li>• Screen resolution, device, browser</li>
                        <li>• Country and language</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-2">Processor:</p>
                      <p className="text-gray-700">Microsoft Corporation, One Microsoft Way, Redmond, WA 98052, USA</p>
                    </div>
                    <p className="text-sm text-gray-600">Microsoft adheres to Privacy Shield framework and Standard Contractual Clauses.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Cookie Consent & Settings */}
            <div className="mb-16 pb-16 border-b border-gray-200">
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-6">Cookie Consent & Settings</h3>
              <div className="space-y-6">
                <div>
                  <p className="font-semibold text-gray-900 mb-2">Our Approach:</p>
                  <p className="text-gray-700 leading-relaxed">
                    Our website operates on a consent basis. We do not use non-essential cookies (Analytics, Marketing) unless you grant permission. If you previously consented but wish to withdraw or modify your cookie settings, you can do so at any time through the cookie banner or by updating your preferences directly in your browser.
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-gray-900 mb-2">Withdraw Consent:</p>
                  <p className="text-gray-700 leading-relaxed">
                    You have the right to withdraw your consent to analytics and marketing cookies at any time. Simply access the cookie settings through our cookie consent banner.
                  </p>
                </div>
              </div>
            </div>

            {/* Data We Collect */}
            <div className="mb-16 pb-16 border-b border-gray-200">
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-6">Personal Data We Collect</h3>
              <div className="space-y-6">
                <div>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    When you visit our website, we may collect the following categories of personal data:
                  </p>
                  <ul className="space-y-4 ml-4">
                    <li className="text-gray-700">
                      <span className="font-semibold">Technical Data:</span> IP address, browser type, pages visited, cookies stored, device information.
                    </li>
                    <li className="text-gray-700">
                      <span className="font-semibold">Contact Information:</span> Name, email, message (only if you voluntarily fill out the contact form).
                    </li>
                    <li className="text-gray-700">
                      <span className="font-semibold">Analytics Data:</span> User behavior, session duration, traffic sources (anonymized via Google Analytics and Microsoft Clarity).
                    </li>
                  </ul>
                </div>
                <div>
                  <p className="font-semibold text-gray-900 mb-2">Data Usage:</p>
                  <p className="text-gray-700 leading-relaxed">
                    We do not sell your personal data and use it only for the purposes stated in this policy. All data processing is conducted in compliance with applicable data protection regulations.
                  </p>
                </div>
              </div>
            </div>

            {/* Your Rights Under GDPR */}
            <div className="mb-16 pb-16 border-b border-gray-200">
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-6">Your Rights Under GDPR</h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                As a data subject in the European Union, you have the following rights:
              </p>
              <ul className="space-y-3 ml-4">
                <li className="text-gray-700"><span className="font-semibold">Right to Access:</span> You can request and receive a copy of your personal data.</li>
                <li className="text-gray-700"><span className="font-semibold">Right to Correction:</span> You can correct inaccuracies in your personal data.</li>
                <li className="text-gray-700"><span className="font-semibold">Right to Erasure:</span> You can request deletion of your personal data (&quot;right to be forgotten&quot;).</li>
                <li className="text-gray-700"><span className="font-semibold">Right to Restrict Processing:</span> You can limit how your data is processed.</li>
                <li className="text-gray-700"><span className="font-semibold">Right to Data Portability:</span> You can receive your data in a portable format.</li>
                <li className="text-gray-700"><span className="font-semibold">Right to Object:</span> You can object to processing based on legitimate interest.</li>
                <li className="text-gray-700"><span className="font-semibold">Right to Withdraw Consent:</span> You can withdraw consent for Analytics and Marketing cookies at any time.</li>
              </ul>
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <p className="font-semibold text-gray-900 mb-2">How to Exercise Your Rights:</p>
                <p className="text-gray-700">
                  To exercise any of these rights, please send an email through our <a href="/contact" className="text-blue-600 hover:underline">Contact page</a> with the subject &quot;GDPR Request&quot;. We respond within 30 days and may ask for identity verification to protect your privacy.
                </p>
              </div>
            </div>

            {/* Data Controller & Processor */}
            <div className="mb-16 pb-16 border-b border-gray-200">
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-6">Data Controller & Processors</h3>
              <div className="space-y-6">
                <div>
                  <p className="font-semibold text-gray-900 mb-2">Data Controller:</p>
                  <p className="text-gray-700 leading-relaxed">
                    The data controller of your personal information is the website operator at BMateas.com. For contact details and to exercise your rights, please visit the <a href="/contact" className="text-blue-600 hover:underline">Contact page</a>.
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-gray-900 mb-2">Data Processors:</p>
                  <ul className="space-y-3 ml-4">
                    <li className="text-gray-700"><span className="font-semibold">Google Ireland Limited</span> - for Google Analytics (GA4)</li>
                    <li className="text-gray-700"><span className="font-semibold">Microsoft Corporation</span> - for Microsoft Clarity</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Data Security */}
            <div className="mb-16 pb-16 border-b border-gray-200">
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-6">Data Security Measures</h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                We implement comprehensive technical and organizational measures to protect your personal data:
              </p>
              <ul className="space-y-3 ml-4">
                <li className="text-gray-700"><span className="font-semibold">Encryption:</span> All data in transit is encrypted using HTTPS/SSL protocols.</li>
                <li className="text-gray-700"><span className="font-semibold">Firewalls & Intrusion Detection:</span> Advanced security systems monitor and prevent unauthorized access.</li>
                <li className="text-gray-700"><span className="font-semibold">Access Controls:</span> Strict user permissions and role-based access control.</li>
                <li className="text-gray-700"><span className="font-semibold">Regular Security Audits:</span> Periodic security assessments and vulnerability testing.</li>
                <li className="text-gray-700"><span className="font-semibold">Secure Hosting:</span> Infrastructure hosted with certified security providers.</li>
              </ul>
              <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-gray-700">
                  While we implement strong security practices, no online system is 100% secure. We commit to addressing any data breach promptly and transparently, in compliance with GDPR requirements.
                </p>
              </div>
            </div>

            {/* International Data Transfers */}
            <div className="mb-16 pb-16 border-b border-gray-200">
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-6">International Data Transfers</h3>
              <p className="text-gray-700 leading-relaxed">
                Your data may be transferred to countries outside the European Economic Area for processing by our service providers. Where such transfers occur, we implement appropriate safeguards including Standard Contractual Clauses (SCCs) to ensure your data is protected in accordance with GDPR.
              </p>
            </div>

            {/* Cookie Management */}
            <div className="mb-16 pb-16 border-b border-gray-200">
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-6">How to Manage Cookies in Your Browser</h3>
              <div className="space-y-6">
                <div>
                  <p className="font-semibold text-gray-900 mb-3">Delete Cookies:</p>
                  <p className="text-gray-700 mb-4">You can delete cookies stored in your browser through your browser settings:</p>
                  <ul className="space-y-2 ml-4">
                    <li className="text-gray-700"><span className="font-semibold">Chrome:</span> <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">support.google.com/chrome/answer/95647</a></li>
                    <li className="text-gray-700"><span className="font-semibold">Firefox:</span> <a href="https://support.mozilla.org/kb/clear-browsing-data-firefox" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">support.mozilla.org/kb/clear-browsing-data-firefox</a></li>
                    <li className="text-gray-700"><span className="font-semibold">Edge/IE:</span> <a href="https://support.microsoft.com/en-us/windows/delete-and-manage-cookies" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">support.microsoft.com/en-us/windows/delete-and-manage-cookies</a></li>
                  </ul>
                </div>
                <div>
                  <p className="font-semibold text-gray-900 mb-3">Block Cookies:</p>
                  <p className="text-gray-700">
                    Most browsers allow you to block cookies or receive warnings before they are set. However, blocking or disabling cookies may limit website functionality. For optimal experience, we recommend allowing cookies while carefully reviewing what permissions you grant.
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-gray-900 mb-3">Do Not Track:</p>
                  <p className="text-gray-700">
                    Some browsers support the &quot;Do Not Track&quot; (DNT) feature. We respect user privacy choices, but please note that not all third-party services honor DNT signals.
                  </p>
                </div>
              </div>
            </div>

            {/* Policy Updates */}
            <div>
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-6">Changes to This Policy</h3>
              <p className="text-gray-700 leading-relaxed">
                We may update this Cookies & Privacy Policy when legislation changes, we update our services, or our practices evolve. The latest version is always available on this page with an effective date. Significant changes will be communicated via email notification or prominent banner on the website. Your continued use of the website after any changes constitutes your acceptance of the updated policy.
              </p>
            </div>
          </div>

          {/* Cookies Table - CookieHub Declaration */}
          <div className="mb-20">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-12">Cookies Used on This Website</h2>
            <div className="cookiehub-declaration"></div>
          </div>

          {/* CTA Section */}
          <div className="page-panel page-panel--soft p-12 text-center">
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Questions About Your Data?</h3>
            <p className="text-gray-700 mb-8 max-w-2xl mx-auto">
              If you have any concerns about our cookie or privacy practices, or if you wish to exercise your rights under GDPR, please reach out to us.
            </p>
            <CTAButton href="/contact" variant="filled">
              Contact me
            </CTAButton>
          </div>

          {/* Last Updated */}
          <div className="mt-12 text-center text-gray-500 text-sm">
            <p>Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
